import React from 'react';
import { Link } from 'react-router-dom';
import { BookmarkIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Panel } from '../../ui/Panel';
import { SaveButton } from '../SaveButton';
import { UniversityMark } from '../UniversityMark';
import { tuitionLabel } from '../CompareTray';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { universityProfileById } from '../../../data/admissions';
import { deadlineState } from '../../../utils/discover';

/**
 * Everything the student has kept, in one place.
 *
 * Saved items survive the presentation changing — this reads the same state the
 * cards write to, so an institution saved in one composition is saved in both.
 */
export function SavedSection() {
  const {
    savedUniversities,
    savedProgrammes,
    savedScholarships,
    savedCount,
    toggleSave,
    setSection
  } = useDiscover();
  if (savedCount === 0) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-1603" data-mp="e6lq2bpj5tru">
        <EmptyState icon={<BookmarkIcon className="h-5 w-5" data-id="element-1605" data-mp="97sdqmlzc8t9" />} title="Nothing saved yet" description="Saving an institution, programme or award keeps it here and shapes what your admissions feed tells you about. Nothing you save is visible to anyone else." action={<Button variant="primary" onClick={() => setSection('Universities')} data-id="element-1606" data-mp="3rdn4apbtx268">
              Browse universities
            </Button>} data-id="element-1604" data-mp="993h4lbijeju" />
      </div>;
  }
  return <div className="space-y-5" data-id="element-1607" data-mp="56yjzcluvzsg">
      {savedUniversities.length > 0 && <Panel title="Saved institutions" bodyClassName="p-0" data-id="element-1608" data-mp="54vowe5ujggv">
          <ul className="divide-y divide-line" data-id="element-1609" data-mp="6kd3xvzqvw0">
            {savedUniversities.map(university => <li key={university.id} className="flex items-center gap-4 px-5 py-4" data-id="element-1610" data-mp="1gku30wfvssoy">
                <UniversityMark university={university} size="sm" data-id="element-1611" data-mp="1bmir8g610843" />
                <div className="min-w-0 flex-1" data-id="element-1612" data-mp="1ir0el82da255">
                  <Link to={`/discover/universities/${university.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-1613" data-mp="1iq8uoui4tsup">
                    {university.name}
                  </Link>
                  <p className="text-body-sm text-ink-muted" data-id="element-1614" data-mp="1xoawaz5ls49b">
                    {university.city}, {university.country} · {university.type}
                  </p>
                </div>
                <SaveButton saved onToggle={() => toggleSave('university', university.id)} subject={university.name} data-id="element-1615" data-mp="14yu8yfwubdjy" />
              </li>)}
          </ul>
        </Panel>}

      {savedProgrammes.length > 0 && <Panel title="Saved programmes" bodyClassName="p-0" data-id="element-1616" data-mp="h9rhkgcem6c9">
          <ul className="divide-y divide-line" data-id="element-1617" data-mp="h99fv46z2zwy">
            {savedProgrammes.map(programme => {
          const state = deadlineState(programme.deadline);
          return <li key={programme.id} className="flex items-center gap-4 px-5 py-4" data-id="element-1618" data-mp="2zicdu89dbfz9">
                  <div className="min-w-0 flex-1" data-id="element-1619" data-mp="2vilo87vkv5wb">
                    <Link to={`/discover/programmes/${programme.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-1620" data-mp="2qi7emlebvnw6">
                      {programme.title}
                    </Link>
                    <p className="cz-num text-body-sm text-ink-muted" data-id="element-1621" data-mp="2glb3s90yipog">
                      {universityProfileById(programme.universityId)?.name} ·{' '}
                      {tuitionLabel(programme)}
                    </p>
                  </div>
                  <Badge tone={state.tone === 'neutral' ? 'neutral' : state.tone} plain data-id="element-1622" data-mp="22t503mezqb5w">
                    {state.label}
                  </Badge>
                  <SaveButton saved onToggle={() => toggleSave('programme', programme.id)} subject={programme.title} data-id="element-1623" data-mp="1xsqq3yttwr58" />
                </li>;
        })}
          </ul>
        </Panel>}

      {savedScholarships.length > 0 && <Panel title="Saved awards" bodyClassName="p-0" data-id="element-1624" data-mp="1s8aiv0n7cj8">
          <ul className="divide-y divide-line" data-id="element-1625" data-mp="3j63wq2pnk4tw">
            {savedScholarships.map(scholarship => <li key={scholarship.id} className="flex items-center gap-4 px-5 py-4" data-id="element-1626" data-mp="3o4dr4b3jz1eg">
                <div className="min-w-0 flex-1" data-id="element-1627" data-mp="3a5e395nv6qjc">
                  <Link to={`/discover/scholarships/${scholarship.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-1628" data-mp="3f5a7n60h4jzf">
                    {scholarship.name}
                  </Link>
                  <p className="cz-num text-body-sm text-ink-muted" data-id="element-1629" data-mp="357mcwg2vbch9">
                    {scholarship.amountLabel} · {scholarship.deadlineLabel}
                  </p>
                </div>
                <SaveButton saved onToggle={() => toggleSave('scholarship', scholarship.id)} subject={scholarship.name} data-id="element-1630" data-mp="13eob3ur459k6" />
              </li>)}
          </ul>
        </Panel>}
    </div>;
}